import { observable, action, reaction } from 'mobx';
import { unformat } from 'accounting';
import * as moment from 'moment';
import { app } from './../App';
import { Lead, LeadContacts } from '.';
import {
  StatusOfBusinessPartner,
  IBusinessPartnerRequest,
} from 'common/interfaces/businessPartner';

// Store
export class BusinessPartner {
  constructor(private lead: Lead, private leadContacts: LeadContacts) {}

  private reactToOpenLeadChange = reaction(
    () => this.lead.openLeadId,
    (id: number) => {
      this.clearAll();
    }
  );

  private get licenseExpDateFormat(): string {
    return 'MMM Do, YYYY';
  }

  @observable public createBusinessPartnerRequestLoading: boolean = false;
  @observable public createBusinessPartnerRequestFetched: boolean = false;

  @observable public newRequest: IBusinessPartnerRequest = {
    showModal: false,
    status: StatusOfBusinessPartner.None,
    isNew: true,
    promoted: '',

    shippingAddress1: '',
    shippingAddress2: '',
    shippingCity: '',
    shippingState: '',
    shippingZipcode: '',

    licenceNumber: '',
    licenceExp: {
      date: moment(),
      label: moment().format(this.licenseExpDateFormat),
    },
  };

  @action public showBusinessPartnerRequestModal = () => {
    this.clearAll();
    this.newRequest.showModal = true;
  };

  @action public hideBusinessPartnerRequestModal = () => {
    this.newRequest.showModal = false;
    this.clearAll();
  };

  @action public copyShippingAddress = () => {
    const { openLeadDetails } = this.lead;

    this.newRequest.shippingAddress1 = openLeadDetails.plAddress1 || '';
    this.newRequest.shippingAddress2 = openLeadDetails.plAddress2 || '';
    this.newRequest.shippingCity = openLeadDetails.plCity || '';
    this.newRequest.shippingState = openLeadDetails.plState || '';
    this.newRequest.shippingZipcode = openLeadDetails.plZipcode
      ? openLeadDetails.plZipcode.toString()
      : '';
  };

  // Change date
  @action public changeLicenseExpirationDate = (date: moment.Moment) => {
    this.newRequest.licenceExp.date = date;
    this.newRequest.licenceExp.label = date.format(this.licenseExpDateFormat);
  };

  // Create pricing request
  @action public createBusinessPartner = async (): Promise<{}> => {
    try {
      this.newRequest.status = StatusOfBusinessPartner.Saving;

      // Get all items visible to the user
      const service = await app.service('provider-to-customer');
      const result = await service.create({
        providerId: this.lead.openLeadId,
        license: unformat(this.newRequest.licenceNumber),
        licenseExpDate: this.newRequest.licenceExp.date.format('YYYY-MM-DD'),
        address: [
          {
            address: 'Shipping',
            street: this.newRequest.shippingAddress1,
            block: this.newRequest.shippingAddress2,
            city: this.newRequest.shippingCity,
            state: this.newRequest.shippingState,
            zipcode: this.newRequest.shippingZipcode,
            country: 'US',
            addressType: 'S',
          },
          {
            address: 'Billing',
            street: this.lead.openLeadDetails.plAddress1,
            block: this.lead.openLeadDetails.plAddress2,
            city: this.lead.openLeadDetails.plCity,
            state: this.lead.openLeadDetails.plState,
            zipcode: this.lead.openLeadDetails.plZipcode,
            country: 'US',
            addressType: 'B',
          },
        ],
      });

      this.newRequest.status = StatusOfBusinessPartner.Success;

      this.lead.openLeadDetails.cardcode = result.cardcode;
      this.lead.openLeadDetails.customerCreated = true;

      setTimeout(() => {
        this.hideBusinessPartnerRequestModal();
      }, 512);

      this.newRequest.promoted = 'Promoted to business partner';

      return result;
    } catch (err) {
      if (err === 'Customer already created for provider') {
        this.newRequest.status =
          StatusOfBusinessPartner.ErrorCustomerAlreadyCreated;
        this.lead.fetchLeadDetails(this.lead.openLeadId);
      } else {
        this.newRequest.status = StatusOfBusinessPartner.ErrorTryAgain;
      }
      console.error(err);
      return err;
    }
  };

  // Fetch item by provider id
  @action public clearAll = () => {
    this.newRequest = {
      showModal: false,
      status: StatusOfBusinessPartner.None,
      isNew: true,
      promoted: '',

      shippingAddress1: '',
      shippingAddress2: '',
      shippingCity: '',
      shippingState: '',
      shippingZipcode: '',

      licenceNumber: '',
      licenceExp: {
        date: moment(),
        label: moment().format(this.licenseExpDateFormat),
      },
    };

    this.createBusinessPartnerRequestLoading = false;
    this.createBusinessPartnerRequestFetched = false;
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/BusinessPartner.ts